import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getSoldGoodsPackagingListQueryKey,
  getSoldGoodsPackagingRecordsListQueryKey,
  useSoldGoodsDestroy,
  useSoldGoodsPackagingCoverageRetrieve,
  useSoldGoodsPackagingList,
  useSoldGoodsPackagingPartialUpdate,
  useSoldGoodsPackagingPreviewRetrieve,
  useSoldGoodsPackagingRecordsList,
  useSoldGoodsPackagingRecordsPartialUpdate,
  useSoldGoodsPackagingRecordsUpdate,
  useSoldGoodsPackagingRetrieve,
  useSoldGoodsPackagingUpdate,
  useSoldGoodsRecordsDestroy,
} from '@shared/api/lib/sold-goods/sold-goods';

import {
  ActivityCoverage,
  PackagingActivityRecordUpdate,
  PackagingActivityRetrieve,
  PackagingActivityUpdate,
  PaginatedPackagingOperationActivityRecordList,
  PaginatedPackagingOperationActivitiesListList,
  SoldGoodsPackagingListParams,
  SoldGoodsPackagingPreviewRetrieveParams,
  SoldGoodsPackagingRecordsListParams,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';

export const useGetSoldPackagingList = (
  params?: SoldGoodsPackagingListParams
) => {
  return useSoldGoodsPackagingList<PaginatedPackagingOperationActivitiesListList>(
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
      },
    }
  );
};

export const useGetSoldPackagingItem = ({
  activityUuid,
}: {
  activityUuid: string;
}) => {
  return useSoldGoodsPackagingRetrieve<PackagingActivityRetrieve>(
    activityUuid,
    {
      request: useAxiosConfig(),
    }
  );
};

export const useUpdateSoldPackagingItem = () => {
  return useSoldGoodsPackagingUpdate<PackagingActivityUpdate>({
    request: useAxiosConfig(),
  });
};

export const usePatchSoldPackagingItem = () => {
  return useSoldGoodsPackagingPartialUpdate<PackagingActivityUpdate>({
    request: useAxiosConfig(),
  });
};

export const useGetSoldPackagingPreview = ({
  params,
  enabled,
}: {
  params?: SoldGoodsPackagingPreviewRetrieveParams;
  enabled?: boolean;
}) => {
  return useSoldGoodsPackagingPreviewRetrieve(params, {
    request: useAxiosConfig(),
    query: {
      enabled: enabled,
      cacheTime: 0,
      staleTime: 0,
    },
  });
};

export const useDeleteSoldPackagingItem = () => {
  const queryClient = useQueryClient();
  return useSoldGoodsDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () =>
        refreshQueries(queryClient, getSoldGoodsPackagingListQueryKey()),
    },
  });
};

export const useGetSoldPackagingItemRecords = ({
  activityUuid,
  params,
}: {
  activityUuid: string;
  params?: SoldGoodsPackagingRecordsListParams;
}) => {
  return useSoldGoodsPackagingRecordsList<PaginatedPackagingOperationActivityRecordList>(
    activityUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
      },
    }
  );
};

export const useUpdateSoldPackagingItemRecord = () => {
  const queryClient = useQueryClient();
  return useSoldGoodsPackagingRecordsUpdate<PackagingActivityRecordUpdate>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (_, variables) => {
        await queryClient.refetchQueries(
          getSoldGoodsPackagingRecordsListQueryKey(variables.activityUuid)
        );
      },
    },
  });
};

export const usePatchSoldPackagingItemRecord = () => {
  const queryClient = useQueryClient();
  return useSoldGoodsPackagingRecordsPartialUpdate<PackagingActivityRecordUpdate>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: (_, variables) => {
          queryClient.refetchQueries(
            getSoldGoodsPackagingRecordsListQueryKey(variables.activityUuid)
          );
        },
      },
    }
  );
};

export const useDeleteSoldPackagingItemRecord = () => {
  const queryClient = useQueryClient();
  return useSoldGoodsRecordsDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (_, variables) => {
        await queryClient.refetchQueries(
          getSoldGoodsPackagingRecordsListQueryKey(variables.activityUuid)
        );
      },
    },
  });
};

export const useGetSoldPackagingCoverage = () => {
  return useSoldGoodsPackagingCoverageRetrieve<ActivityCoverage>({
    request: useAxiosConfig(),
  });
};
